// src/modules/operaciones/inventarios/dashboard/PanelAlertas.js
//
// Pestaña "Alertas" del dashboard de mermas:
//   1. Validaciones contra el archivo: ¿los totales calculados coinciden con las columnas
//      TOTAL MERMA y con la fila "Total general" del Excel? (✓ verde / ✗ rojo con la diferencia)
//   2. Alertas de calidad de datos agrupadas por nivel (error, advertencia, info, ok),
//      con título, detalle y los primeros ejemplos.
//
// USO
//   <PanelAlertas analisis={d.analisis} />
//
// Espera analisis.validaciones y analisis.alertas (salida de analizarMermas):
//   validacion: { id, etiqueta, calculado, esperado, diferencia, ok, detalle }
//   alerta:     { id, nivel: "error"|"advertencia"|"info"|"ok", titulo, detalle, cantidad,
//                 ejemplos: [{ item, descripcion, fabricante, valor }], formato: null|"porcentaje"|"moneda" }
//
// NOTAS
//  - Las validaciones y alertas se calculan sobre el archivo COMPLETO, sin filtros: no cambian
//    al filtrar el dashboard.
//  - La alerta con id "validacion" es un resumen de las validaciones; como estas ya se muestran
//    en su propia sección, no se repite en la lista.

import React, { useState } from "react";
import { AlertTriangle, CheckCircle2, ChevronDown, Info, ShieldCheck, XCircle } from "lucide-react";

import { fmtMoneda, fmtNumero, fmtPorcentaje, truncar } from "../utils/informesComun";

/* ========================================================================== */
/* Configuración por nivel                                                    */
/* ========================================================================== */

const NIVELES = [
  { id: "error", titulo: "Errores", icono: XCircle, borde: "border-l-red-500", icon: "text-red-500", chip: "bg-red-50 text-red-700" },
  { id: "advertencia", titulo: "Advertencias", icono: AlertTriangle, borde: "border-l-amber-500", icon: "text-amber-500", chip: "bg-amber-50 text-amber-700" },
  { id: "info", titulo: "Información", icono: Info, borde: "border-l-blue-400", icon: "text-blue-500", chip: "bg-blue-50 text-blue-700" },
  { id: "ok", titulo: "Correcto", icono: CheckCircle2, borde: "border-l-emerald-500", icon: "text-emerald-500", chip: "bg-emerald-50 text-emerald-700" },
];

const MAX_EJEMPLOS = 5;

// Valor de un ejemplo: número según el formato de la alerta, o texto tal cual ("UNID → UND")
function textoValor(valor, formato) {
  if (valor === null || valor === undefined || valor === "") return null;
  if (typeof valor !== "number") return String(valor);
  if (formato === "porcentaje") return fmtPorcentaje(valor, 2);
  if (formato === "moneda") return fmtMoneda(valor);
  return fmtNumero(valor);
}

/* ========================================================================== */
/* Validaciones                                                               */
/* ========================================================================== */

function FilaValidacion({ v }) {
  const Icono = v.ok ? CheckCircle2 : XCircle;
  return (
    <li className="flex items-start gap-3 py-3">
      <Icono
        className={`w-5 h-5 flex-shrink-0 mt-0.5 ${v.ok ? "text-emerald-500" : "text-red-500"}`}
        aria-label={v.ok ? "Coincide" : "No coincide"}
      />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900">{v.etiqueta}</p>
        <p className="text-xs text-gray-500 mt-0.5 tabular-nums">
          Calculado {fmtMoneda(v.calculado)} · Archivo {fmtMoneda(v.esperado)}
        </p>
        {!v.ok && (
          <p className="text-xs font-semibold text-red-600 mt-0.5 tabular-nums">
            Diferencia: {fmtMoneda(v.diferencia, { decimales: 2, signo: true })}
          </p>
        )}
        {v.detalle && <p className="text-xs text-gray-500 mt-0.5">{v.detalle}</p>}
      </div>
      <span
        className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold ${
          v.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"
        }`}
      >
        {v.ok ? "Cuadra" : "No cuadra"}
      </span>
    </li>
  );
}

function SeccionValidaciones({ validaciones }) {
  return (
    <section className="bg-white border border-gray-200 rounded-xl shadow-sm p-4" aria-labelledby="titulo-validaciones">
      <h3 id="titulo-validaciones" className="text-sm font-semibold text-gray-900">
        Validaciones contra el archivo
      </h3>
      <p className="text-xs text-gray-500 mt-0.5 mb-1">
        Compara lo que calcula el dashboard con las columnas TOTAL MERMA y la fila Total general del Excel.
      </p>
      {validaciones.length === 0 ? (
        <p className="text-sm text-gray-500 py-3">
          El archivo no trae columnas TOTAL MERMA ni fila Total general para comparar.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100">
          {validaciones.map((v) => (
            <FilaValidacion key={v.id} v={v} />
          ))}
        </ul>
      )}
    </section>
  );
}

/* ========================================================================== */
/* Alertas                                                                    */
/* ========================================================================== */

function TarjetaAlerta({ alerta, nivel }) {
  const [verTodos, setVerTodos] = useState(false);
  const Icono = nivel.icono;
  const ejemplos = alerta.ejemplos || [];
  const visibles = verTodos ? ejemplos : ejemplos.slice(0, MAX_EJEMPLOS);
  const restantes = typeof alerta.cantidad === "number" ? alerta.cantidad - visibles.length : 0;

  return (
    <article className={`bg-white border border-gray-200 border-l-4 ${nivel.borde} rounded-lg shadow-sm p-3`}>
      <div className="flex items-start gap-2.5">
        <Icono className={`w-5 h-5 flex-shrink-0 mt-0.5 ${nivel.icon}`} aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h4 className="text-sm font-semibold text-gray-900 leading-snug">{alerta.titulo}</h4>
            {typeof alerta.cantidad === "number" && alerta.cantidad > 0 && ejemplos.length > 0 && (
              <span className={`flex-shrink-0 rounded-full px-2 py-0.5 text-xs font-semibold tabular-nums ${nivel.chip}`}>
                {fmtNumero(alerta.cantidad)}
              </span>
            )}
          </div>
          {alerta.detalle && <p className="text-xs text-gray-600 mt-1 leading-snug">{alerta.detalle}</p>}

          {ejemplos.length > 0 && (
            <ul className="mt-2 divide-y divide-gray-100 border border-gray-100 rounded-md">
              {visibles.map((e, i) => {
                const valor = textoValor(e.valor, alerta.formato);
                return (
                  <li key={`${e.item}-${i}`} className="flex items-center justify-between gap-3 px-2 py-1.5 text-xs">
                    <span className="min-w-0 text-gray-700">
                      {e.item && <span className="font-mono text-gray-400 mr-1.5">{e.item}</span>}
                      <span title={e.descripcion}>{truncar(e.descripcion, 44)}</span>
                      {e.fabricante && <span className="text-gray-400"> · {truncar(e.fabricante, 24)}</span>}
                    </span>
                    {valor && <span className="flex-shrink-0 font-semibold tabular-nums text-gray-900">{valor}</span>}
                  </li>
                );
              })}
            </ul>
          )}

          {ejemplos.length > MAX_EJEMPLOS && (
            <button
              type="button"
              onClick={() => setVerTodos((v) => !v)}
              className="mt-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 focus:outline-none focus:underline"
            >
              {verTodos ? "Ver menos" : `Ver ${ejemplos.length - MAX_EJEMPLOS} más`}
            </button>
          )}
          {ejemplos.length > 0 && restantes > 0 && (
            <p className="mt-1 text-xs text-gray-400">y {fmtNumero(restantes)} más en el archivo</p>
          )}
        </div>
      </div>
    </article>
  );
}

function GrupoAlertas({ nivel, alertas, plegable, abiertoInicial }) {
  const [abierto, setAbierto] = useState(abiertoInicial);
  const Icono = nivel.icono;

  const encabezado = (
    <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
      <Icono className={`w-4 h-4 ${nivel.icon}`} aria-hidden="true" />
      {nivel.titulo}
      <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${nivel.chip}`}>{alertas.length}</span>
    </span>
  );

  return (
    <section aria-label={nivel.titulo}>
      {plegable ? (
        <button
          type="button"
          onClick={() => setAbierto((v) => !v)}
          aria-expanded={abierto}
          className="w-full flex items-center justify-between mb-2 focus:outline-none focus:underline"
        >
          {encabezado}
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${abierto ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      ) : (
        <div className="mb-2">{encabezado}</div>
      )}
      {abierto && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          {alertas.map((a) => (
            <TarjetaAlerta key={a.id} alerta={a} nivel={nivel} />
          ))}
        </div>
      )}
    </section>
  );
}

/* ========================================================================== */
/* Resumen general y estados vacíos                                           */
/* ========================================================================== */

function Banner({ tipo, titulo, texto }) {
  const estilos = {
    ok: { caja: "bg-emerald-50 border-emerald-200 text-emerald-900", icono: ShieldCheck, color: "text-emerald-500" },
    aviso: { caja: "bg-amber-50 border-amber-200 text-amber-900", icono: AlertTriangle, color: "text-amber-500" },
    error: { caja: "bg-red-50 border-red-200 text-red-900", icono: XCircle, color: "text-red-500" },
  }[tipo];
  const Icono = estilos.icono;
  return (
    <div className={`flex items-start gap-3 border rounded-xl px-4 py-3 ${estilos.caja}`} role="status">
      <Icono className={`w-6 h-6 flex-shrink-0 ${estilos.color}`} aria-hidden="true" />
      <div>
        <p className="text-sm font-semibold">{titulo}</p>
        {texto && <p className="text-xs mt-0.5 opacity-90">{texto}</p>}
      </div>
    </div>
  );
}

function TodoEnOrden() {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-emerald-200 rounded-xl py-14 px-4">
      <ShieldCheck className="w-12 h-12 text-emerald-400 mb-3" aria-hidden="true" />
      <p className="text-base font-semibold text-gray-900">Todo en orden</p>
      <p className="text-sm text-gray-500 mt-1">No se encontraron alertas ni diferencias en el archivo.</p>
    </div>
  );
}

function SinDatos() {
  return (
    <div className="flex flex-col items-center justify-center text-center bg-white border border-dashed border-gray-300 rounded-xl py-16 px-4">
      <Info className="w-10 h-10 text-gray-300 mb-3" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-700">No hay datos</p>
      <p className="text-xs text-gray-500 mt-1">Sube un informe de mermas para ver las validaciones y alertas.</p>
    </div>
  );
}

/* ========================================================================== */
/* Panel                                                                      */
/* ========================================================================== */

export function PanelAlertas({ analisis }) {
  if (!analisis || !Array.isArray(analisis.alertas)) return <SinDatos />;

  const validaciones = Array.isArray(analisis.validaciones) ? analisis.validaciones : [];
  // La alerta "validacion" resume las validaciones, que ya tienen su propia sección
  const alertas = analisis.alertas.filter((a) => a.id !== "validacion");

  if (validaciones.length === 0 && alertas.length === 0) return <TodoEnOrden />;

  const fallidas = validaciones.filter((v) => !v.ok).length;
  const porNivel = Object.fromEntries(NIVELES.map((n) => [n.id, alertas.filter((a) => a.nivel === n.id)]));
  const errores = porNivel.error.length + fallidas;
  const advertencias = porNivel.advertencia.length;

  let banner;
  if (errores > 0) {
    banner = (
      <Banner
        tipo="error"
        titulo={`Hay ${fmtNumero(errores)} problema(s) que revisar`}
        texto={fallidas > 0 ? "Algunos totales calculados no coinciden con los del archivo." : undefined}
      />
    );
  } else if (advertencias > 0) {
    banner = (
      <Banner
        tipo="aviso"
        titulo={`${fmtNumero(advertencias)} advertencia(s) de calidad de datos`}
        texto={validaciones.length > 0 ? "Los totales calculados coinciden con los del archivo." : undefined}
      />
    );
  } else {
    banner = (
      <Banner
        tipo="ok"
        titulo="Todo en orden"
        texto={validaciones.length > 0 ? "Los totales coinciden con el archivo y no hay advertencias." : "No hay advertencias."}
      />
    );
  }

  return (
    <div className="space-y-4">
      {banner}
      <p className="text-xs text-gray-500 px-1">
        Las validaciones y alertas se calculan sobre el archivo completo; no cambian con los filtros.
      </p>

      <SeccionValidaciones validaciones={validaciones} />

      {alertas.length > 0 && (
        <div className="space-y-5">
          <h3 className="text-sm font-semibold text-gray-900 px-1">Alertas de calidad de datos</h3>
          {NIVELES.map((nivel) => {
            const lista = porNivel[nivel.id];
            if (lista.length === 0) return null;
            const plegable = nivel.id === "info" || nivel.id === "ok";
            return (
              <GrupoAlertas
                key={nivel.id}
                nivel={nivel}
                alertas={lista}
                plegable={plegable}
                abiertoInicial={!plegable || lista.length <= 3}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

export default PanelAlertas;